import { getDefaultShopTimezone } from '../utils/date.util';

export interface TimezoneOption {
  /** Full label with UTC offset (Add Location modal). */
  label: string;
  /** Compact label (profile Address edit form). */
  shortLabel: string;
  /** IANA timezone identifier sent to location add/edit APIs (e.g. America/Los_Angeles). */
  value: string;
}

/** Format the current UTC offset for an IANA timezone (accounts for DST). */
function formatCurrentUtcOffset(ianaTimezone: string, at = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ianaTimezone,
    timeZoneName: 'longOffset',
  }).formatToParts(at);
  const gmt = parts.find((part) => part.type === 'timeZoneName')?.value;
  if (!gmt) {
    return '';
  }
  const match = gmt.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/);
  if (!match) {
    return gmt.replace(/^GMT/, 'UTC');
  }
  const [, sign, hours, minutes = '00'] = match;
  return `UTC${sign}${hours.padStart(2, '0')}:${minutes.padEnd(2, '0')}`;
}

const US_TIMEZONE_ENTRIES: readonly {
  name: string;
  labelSuffix?: string;
  shortSuffix?: string;
  value: string;
}[] = [
  { name: 'Eastern Standard Time (EST)', value: 'America/New_York' },
  { name: 'Central Standard Time (CST)', value: 'America/Chicago' },
  { name: 'Mountain Standard Time (MST)', value: 'America/Denver' },
  {
    name: 'Mountain Standard Time (MST)',
    labelSuffix: ' (Arizona, no DST)',
    shortSuffix: ' — Arizona',
    value: 'America/Phoenix',
  },
  { name: 'Pacific Standard Time (PST)', value: 'America/Los_Angeles' },
  { name: 'Alaska Standard Time (AKST)', value: 'America/Anchorage' },
  { name: 'Hawaii Standard Time (HST)', value: 'Pacific/Honolulu' },
  { name: 'Atlantic Standard Time (AST)', value: 'America/Puerto_Rico' },
  { name: 'Samoa Standard Time', value: 'Pacific/Pago_Pago' },
  { name: 'Chamorro Standard Time', value: 'Pacific/Guam' },
];

/** Default timezone for new location forms (Pacific Standard Time). */
export const DEFAULT_LOCATION_TIMEZONE = 'America/Los_Angeles';

/** US timezone options for location forms. `value` is the IANA Region/City identifier. */
export const US_TIMEZONES: TimezoneOption[] = US_TIMEZONE_ENTRIES.map((entry) => {
  const labelSuffix = entry.labelSuffix ?? '';
  const shortSuffix = entry.shortSuffix ?? labelSuffix;
  return {
    label: `${entry.name}${labelSuffix} — ${formatCurrentUtcOffset(entry.value)}`,
    shortLabel: `${entry.name}${shortSuffix}`,
    value: entry.value,
  };
});

const US_TIMEZONE_VALUES = new Set(US_TIMEZONES.map((tz) => tz.value));

/** Legacy display strings mapped to IANA (for locations saved before IANA payloads). */
const LEGACY_DISPLAY_TO_IANA: Record<string, string> = {
  'EST (Eastern Standard Time)': 'America/New_York',
  'CST (Central Standard Time)': 'America/Chicago',
  'MST (Mountain Standard Time)': 'America/Denver',
  'MST (Mountain Standard Time, Arizona)': 'America/Phoenix',
  'PST (Pacific Standard Time)': 'America/Los_Angeles',
  'AKST (Alaska Standard Time)': 'America/Anchorage',
  'HST (Hawaii Standard Time)': 'Pacific/Honolulu',
  'AST (Atlantic Standard Time)': 'America/Puerto_Rico',
  'SST (Samoa Standard Time)': 'Pacific/Pago_Pago',
  'ChST (Chamorro Standard Time)': 'Pacific/Guam',
};

function resolveShopTimezoneFromStorage(): string {
  const fromStorage = getDefaultShopTimezone();
  if (fromStorage && US_TIMEZONE_VALUES.has(fromStorage)) {
    return fromStorage;
  }
  if (fromStorage && LEGACY_DISPLAY_TO_IANA[fromStorage]) {
    return LEGACY_DISPLAY_TO_IANA[fromStorage];
  }
  return fromStorage || DEFAULT_LOCATION_TIMEZONE;
}

/** Resolve a stored/API timezone to a supported IANA value; falls back to shop default from localStorage. */
export function resolveLocationTimezone(timezone: string | null | undefined): string {
  const normalized = typeof timezone === 'string' ? timezone.trim() : '';
  if (!normalized) {
    return resolveShopTimezoneFromStorage();
  }
  if (US_TIMEZONE_VALUES.has(normalized)) {
    return normalized;
  }
  return LEGACY_DISPLAY_TO_IANA[normalized] ?? resolveShopTimezoneFromStorage();
}
